import { GameState, IncomingBid, Player, TeamInLeague } from '../domain/entities';
import { getPlayerValue } from '../domain/constants';

/**
 * AIBiddingService
 * Handles AI teams scouting and bidding on any player in the game
 */
export const AIBiddingService = {
    /**
     * Process AI team bidding during weekly/seasonal simulation
     * AI teams evaluate available players and place bids on suitable targets
     */
    processAIBidding: (gameState: GameState): GameState => {
        const newState = JSON.parse(JSON.stringify(gameState)) as GameState;

        // Only process bidding periodically (every 2 weeks on management weeks: weeks 1, 5, 9, etc.)
        const isManagementWeek = newState.currentWeek % 4 === 1 || newState.currentWeek === 1;
        if (!isManagementWeek || Math.random() < 0.4) {
            return newState; // 40% chance to skip even on management weeks
        }

        // Get all AI teams
        const aiTeams: TeamInLeague[] = [];
        Object.values(newState.leagues).forEach(league => {
            league.teams
                .filter(t => t.name !== newState.playerTeamName)
                .forEach(t => aiTeams.push(t));
        });

        // Each AI team evaluates potential targets
        aiTeams.forEach(biddingTeam => {
            // Get all available players from other teams
            const potentialTargets: Array<{
                player: Player;
                teamName: string;
                leagueName: string;
            }> = [];

            Object.entries(newState.leagues).forEach(([leagueName, league]) => {
                league.teams.forEach(ownerTeam => {
                    // Don't bid on own players
                    if (ownerTeam.name === biddingTeam.name) return;

                    ownerTeam.squad.forEach(player => {
                        // Don't bid on injured players or those approaching retirement
                        if (player.injury || player.age > 35) return;

                        potentialTargets.push({
                            player,
                            teamName: ownerTeam.name,
                            leagueName
                        });
                    });
                });
            });

            // AI team determines which players to bid on (max 2 bids per week)
            const bidTargets = AIBiddingService.selectBidTargets(
                biddingTeam,
                potentialTargets,
                newState,
                2 // max 2 bids per team per week
            );

            // Create bids for selected targets
            bidTargets.forEach(target => {
                const bidAmount = AIBiddingService.calculateBidAmount(
                    target.player,
                    target.leagueName,
                    newState,
                    biddingTeam
                );

                // Only bid if affordable
                if (bidAmount > 0 && biddingTeam.finances.balance > bidAmount * 1.1) {
                    const bid: IncomingBid = {
                        id: `bid_${Date.now()}_${Math.random()}`,
                        playerId: target.player.id,
                        playerName: target.player.name,
                        playerSkill: target.player.skill,
                        playerPosition: target.player.position,
                        playerAge: target.player.age,
                        biddingTeamName: biddingTeam.name,
                        bidAmount,
                        season: newState.season,
                        week: newState.currentWeek,
                        status: 'pending',
                        negotiationRounds: 0,
                        isRead: false
                    };

                    // Only add bid if it's for the player's team (not for AI-to-AI bids)
                    if (target.teamName === newState.playerTeamName) {
                        newState.incomingBids.push(bid);
                    }
                }
            });
        });

        return newState;
    },

    /**
     * Select which players an AI team should bid on
     */
    selectBidTargets: (
        biddingTeam: TeamInLeague,
        potentialTargets: Array<{ player: Player; teamName: string; leagueName: string }>,
        gameState: GameState,
        maxBids: number
    ) => {
        const squadAvgSkill = biddingTeam.squad.length > 0
            ? biddingTeam.squad.reduce((sum, p) => sum + p.skill, 0) / biddingTeam.squad.length
            : 50;

        // Score each potential target
        const scoredTargets = potentialTargets
            .filter(t => {
                // Only target players who could improve the team
                if (t.player.skill < squadAvgSkill - 5) return false;
                if (t.player.age > 32) return false;
                return true;
            })
            .map(t => ({
                ...t,
                score: AIBiddingService.calculateTargetScore(t.player, biddingTeam, squadAvgSkill)
            }))
            .sort((a, b) => b.score - a.score)
            .slice(0, maxBids);

        return scoredTargets;
    },

    /**
     * Calculate how desirable a player is as a bid target
     */
    calculateTargetScore: (player: Player, biddingTeam: TeamInLeague, squadAvgSkill: number): number => {
        let score = 0;

        // Skill improvement
        const skillImprovement = player.skill - squadAvgSkill;
        score += skillImprovement * 2;

        // Age preference (prefer younger players)
        if (player.age < 24) score += 10;
        else if (player.age < 28) score += 5;
        else if (player.age > 31) score -= 5;

        // Form bonus
        score += player.form * 2;

        // Fitness bonus (healthy players preferred)
        if (!player.injury) score += 3;

        // Add some randomness so bids vary
        score += Math.random() * 5;

        return score;
    },

    /**
     * Calculate bid amount for a player
     * Bids are typically 20-40% higher than base market value
     */
    calculateBidAmount: (
        player: Player,
        leagueName: string,
        gameState: GameState,
        biddingTeam: TeamInLeague
    ): number => {
        const baseValue = getPlayerValue(player, leagueName, gameState.gameType, gameState.config);

        // Bid premium based on player quality
        const skillFactor = Math.max(1, (player.skill - 50) / 20);
        const bidPremium = 1.2 + skillFactor * 0.15; // 20-35% premium depending on skill

        const bidAmount = Math.round(baseValue * bidPremium);

        // Don't bid more than 30% of team's balance
        const maxAffordable = Math.floor(biddingTeam.finances.balance * 0.3);

        return Math.min(bidAmount, maxAffordable);
    },

    /**
     * Handle player accepting a bid
     * Transfers the player to bidding team
     */
    acceptBid: (gameState: GameState, bidId: string): GameState => {
        const newState = JSON.parse(JSON.stringify(gameState)) as GameState;
        const bidIndex = newState.incomingBids.findIndex(b => b.id === bidId);
        if (bidIndex === -1) return newState;

        const bid = newState.incomingBids[bidIndex];
        const playerTeam = Object.values(newState.leagues)
            .flatMap(l => l.teams)
            .find(t => t.name === newState.playerTeamName);

        if (!playerTeam) return newState;

        // Find player in team
        const playerIndex = playerTeam.squad.findIndex(p => p.id === bid.playerId);
        if (playerIndex === -1) return newState;

        // Find bidding team
        const biddingTeam = Object.values(newState.leagues)
            .flatMap(l => l.teams)
            .find(t => t.name === bid.biddingTeamName);

        if (!biddingTeam) return newState;

        // Execute transfer
        const [transferredPlayer] = playerTeam.squad.splice(playerIndex, 1);
        biddingTeam.squad.push(transferredPlayer);

        // Update finances
        playerTeam.finances.balance += bid.bidAmount;
        playerTeam.finances.history.push({
            season: newState.season,
            week: newState.currentWeek,
            event: `Sold ${bid.playerName} to ${bid.biddingTeamName} (bid accepted)`,
            amount: bid.bidAmount
        });

        biddingTeam.finances.balance -= bid.bidAmount;
        biddingTeam.finances.history.push({
            season: newState.season,
            week: newState.currentWeek,
            event: `Purchased ${bid.playerName} from ${newState.playerTeamName}`,
            amount: -bid.bidAmount
        });

        // Update bid status
        newState.incomingBids[bidIndex].status = 'accepted';

        return newState;
    },

    /**
     * Handle player rejecting a bid
     */
    rejectBid: (gameState: GameState, bidId: string): GameState => {
        const newState = JSON.parse(JSON.stringify(gameState)) as GameState;
        const bidIndex = newState.incomingBids.findIndex(b => b.id === bidId);
        if (bidIndex === -1) return newState;

        newState.incomingBids[bidIndex].status = 'rejected';
        return newState;
    },

    /**
     * Handle player negotiating a bid (requesting higher price)
     */
    negotiateBid: (gameState: GameState, bidId: string, percentageIncrease: number): GameState => {
        const newState = JSON.parse(JSON.stringify(gameState)) as GameState;
        const bidIndex = newState.incomingBids.findIndex(b => b.id === bidId);
        if (bidIndex === -1) return newState;

        const bid = newState.incomingBids[bidIndex];

        // Check negotiation round limit (max 3 rounds)
        if (bid.negotiationRounds >= 3) {
            // Limit reached - cannot negotiate further
            return newState;
        }

        const newAmount = Math.round(bid.bidAmount * (1 + percentageIncrease / 100));

        // Record the negotiation and increment the round counter
        newState.incomingBids[bidIndex] = {
            ...bid,
            status: 'negotiating',
            lastNegotiationAmount: newAmount,
            lastNegotiationPercentage: percentageIncrease,
            negotiationRounds: bid.negotiationRounds + 1
        };

        return newState;
    },

    /**
     * AI manager responds to negotiation
     * Decides whether to accept the counter-offer
     */
    respondToNegotiation: (gameState: GameState, bidId: string, accept: boolean): GameState => {
        const newState = JSON.parse(JSON.stringify(gameState)) as GameState;
        const bidIndex = newState.incomingBids.findIndex(b => b.id === bidId);
        if (bidIndex === -1) return newState;

        const bid = newState.incomingBids[bidIndex];

        if (accept && bid.lastNegotiationAmount) {
            // Update bid amount to negotiated amount and execute transfer
            newState.incomingBids[bidIndex].bidAmount = bid.lastNegotiationAmount;
            newState.incomingBids[bidIndex].status = 'pending';
            return AIBiddingService.acceptBid(newState, bidId);
        } else {
            // Reject the negotiation
            newState.incomingBids[bidIndex].status = 'rejected';
            return newState;
        }
    },

    /**
     * AI teams bid on listed players based on squad needs
     * Only bid on players that would improve their team
     */
    bidOnListedPlayers: (gameState: GameState): GameState => {
        const newState = JSON.parse(JSON.stringify(gameState)) as GameState;

        // Find all listed players
        const listedPlayerIds = new Set<number>(newState.playersForSale);
        const allTeams = Object.values(newState.leagues).flatMap((l: League) => l.teams);

        // For each listed player
        allTeams.forEach(sellingTeam => {
            sellingTeam.squad.forEach(player => {
                if (!listedPlayerIds.has(player.id)) return; // Only process listed players

                // For each AI team, evaluate if they want to bid
                allTeams.forEach(biddingTeam => {
                    // Skip if team is seller or player's team
                    if (biddingTeam.name === sellingTeam.name || biddingTeam.name === newState.playerTeamName) return;

                    // Skip if team is at squad limit
                    if (biddingTeam.squad.length >= 25) return;

                    // Skip if team already has this player
                    if (biddingTeam.squad.some(p => p.id === player.id)) return;

                    // Evaluate if player would improve the team
                    const squadAvgSkill = biddingTeam.squad.length > 0
                        ? biddingTeam.squad.reduce((sum, p) => sum + p.skill, 0) / biddingTeam.squad.length
                        : 50;

                    // Only bid if player is significantly better than squad average or fills a weak position
                    const skillImprovement = player.skill - squadAvgSkill;
                    if (skillImprovement < 3 || player.age > 32) return; // Not a good fit

                    // Calculate bid amount based on player value
                    const playerValue = getPlayerValue(player, '', newState.gameType, newState.config);
                    const bidAmount = Math.floor(playerValue * (0.8 + Math.random() * 0.4)); // 80-120% of value

                    // Only bid if team can afford it
                    if (biddingTeam.finances.balance < bidAmount * 1.1) return;

                    // Place the bid
                    if (!newState.listedPlayerBids) {
                        newState.listedPlayerBids = [];
                    }

                    // Check if this team already bid this week
                    const alreadyBid = newState.listedPlayerBids.some(
                        b => b.playerId === player.id &&
                             b.biddingTeamName === biddingTeam.name &&
                             b.season === newState.season &&
                             b.week === newState.currentWeek
                    );

                    if (!alreadyBid) {
                        newState.listedPlayerBids.push({
                            id: `listed-bid_${Date.now()}_${Math.random()}`,
                            playerId: player.id,
                            playerName: player.name,
                            sellingTeamName: sellingTeam.name,
                            biddingTeamName: biddingTeam.name,
                            bidAmount,
                            season: newState.season,
                            week: newState.currentWeek
                        });
                    }
                });
            });
        });

        // Accept best bids for each listed player
        return AIBiddingService.acceptBestListedPlayerBids(newState);
    },

    /**
     * Accept the best bid for each listed player
     */
    acceptBestListedPlayerBids: (gameState: GameState): GameState => {
        const newState = JSON.parse(JSON.stringify(gameState)) as GameState;

        if (!newState.listedPlayerBids || newState.listedPlayerBids.length === 0) {
            return newState;
        }

        // Group bids by player
        const bidsByPlayer = new Map<number, typeof newState.listedPlayerBids>();
        newState.listedPlayerBids.forEach(bid => {
            if (!bidsByPlayer.has(bid.playerId)) {
                bidsByPlayer.set(bid.playerId, []);
            }
            bidsByPlayer.get(bid.playerId)!.push(bid);
        });

        const allTeams = Object.values(newState.leagues).flatMap((l: League) => l.teams);

        // For each listed player with bids
        bidsByPlayer.forEach((bids, playerId) => {
            // Find the best bid
            const bestBid = bids.reduce((best, current) =>
                current.bidAmount > best.bidAmount ? current : best
            );

            // Find involved teams
            const sellingTeam = allTeams.find(t => t.name === bestBid.sellingTeamName);
            const buyingTeam = allTeams.find(t => t.name === bestBid.biddingTeamName);

            if (!sellingTeam || !buyingTeam) return;

            // Find the player
            const playerIndex = sellingTeam.squad.findIndex(p => p.id === playerId);
            if (playerIndex === -1) return;

            // Execute the transfer
            const [transferredPlayer] = sellingTeam.squad.splice(playerIndex, 1);
            transferredPlayer.isListed = false;
            transferredPlayer.listedDate = undefined;
            transferredPlayer.askingPrice = undefined;
            transferredPlayer.lastTransferredAt = { season: newState.season, week: newState.currentWeek };

            buyingTeam.squad.push(transferredPlayer);

            // Remove from listed players
            newState.playersForSale = newState.playersForSale.filter(id => id !== playerId);

            // Update finances
            sellingTeam.finances.balance += bestBid.bidAmount;
            sellingTeam.finances.history.push({
                season: newState.season,
                week: newState.currentWeek,
                event: `Sold ${bestBid.playerName} to ${buyingTeam.name}`,
                amount: bestBid.bidAmount
            });

            buyingTeam.finances.balance -= bestBid.bidAmount;
            buyingTeam.finances.history.push({
                season: newState.season,
                week: newState.currentWeek,
                event: `Purchased ${bestBid.playerName} from ${sellingTeam.name}`,
                amount: -bestBid.bidAmount
            });

            // Record transfer
            newState.transferHistory.push({
                season: newState.season,
                week: newState.currentWeek,
                playerName: bestBid.playerName,
                playerSkill: transferredPlayer.skill,
                fromTeam: sellingTeam.name,
                toTeam: buyingTeam.name,
                fee: bestBid.bidAmount,
                playerId: playerId,
                playerAge: transferredPlayer.age,
                playerPosition: transferredPlayer.position,
                playerPotential: transferredPlayer.potential
            });
        });

        // Clear old bids
        newState.listedPlayerBids = newState.listedPlayerBids?.filter(
            b => !(b.season === newState.season && b.week === newState.currentWeek)
        ) || [];

        return newState;
    }
};
